import { ChannelsName, ENTITY } from '@/enums';
import { productParams, useFetchProducts } from '@/hooks';
import { OrderEntity } from '@/pages/privatePages/order/types';
import { ProductEntity } from '@/pages/privatePages/product/types';
import { exportExcel, renderCondition } from '@/utils';
import {
  CustomButton,
  CustomColumnType,
  CustomScrollbar,
} from '@amili-v3/amili-v3-design';
import { Checkbox } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { isString } from 'lodash';
import _get from 'lodash/get';
import { GetRowKey } from 'rc-table/lib/interface';
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { CustomModalExportStyle } from './style';

const CheckboxGroup = Checkbox.Group;

interface IModalExportUser {
  entity: string;
  isOpen: boolean;
  closeModal: () => void;
  isSelectAllRow?: boolean;
  unCheckedListArr?: string[];
  data: Record<string, any>[];
  rowKey: string | GetRowKey<any>;
  columns?: CustomColumnType<any>[];
  setAllPageRecords?: Dispatch<SetStateAction<any[]>>;
  setCheckedListOfAllPage?: Dispatch<SetStateAction<Record<number, string[]>>>;
  setCheckedRecordByPage?: React.Dispatch<
    React.SetStateAction<Record<number, any[]>>
  >;
  isSortColumns?: boolean;
  transformData?: (
    orders: OrderEntity[],
    products: ProductEntity[],
  ) => OrderEntity[];
}

const ExportByColumnModalWithoutFetch: FC<IModalExportUser> = props => {
  const {
    data,
    isOpen,
    entity,
    rowKey,
    closeModal: toggleModal,
    isSortColumns,
    columns: columnsTable = [],
    isSelectAllRow,
    unCheckedListArr,
    setAllPageRecords,
    setCheckedListOfAllPage,
    transformData,
    setCheckedRecordByPage,
  } = props;
  const { t } = useTranslation();

  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [indeterminate, setIndeterminate] = useState(false);
  const [checkAll, setCheckAll] = useState(true);

  const { products } = useFetchProducts(productParams, {
    enabled: !!transformData && isOpen,
    staleTime: Infinity,
  });

  const columns = isSortColumns
    ? columnsTable?.sort((a, b) => Number(a?.exportKey) - Number(b?.exportKey))
    : columnsTable;

  const transformCols = useMemo<string[]>(
    () =>
      columns
        .filter(
          column => column?.className !== 'actions' && !column?.isClearOnExport,
        )
        .reduce((res: string[], i) => {
          if (i.title) {
            if (!isString(i.title)) {
              const title = i?.dataIndex?.toString() ?? '';
              const capitalizeTitle =
                title.charAt(0).toUpperCase() + title.slice(1);
              res.push(capitalizeTitle);
            } else res.push(i.title.toString());
          }
          return res;
        }, []),
    [columns],
  );
  useEffect(() => {
    setSelectedValues(transformCols);
  }, [transformCols]);

  const handleClose = useCallback(() => {
    setSelectedValues(transformCols);
    setIndeterminate(false);
    setCheckAll(true);
    toggleModal();
  }, [
    toggleModal,
    setCheckAll,
    transformCols,
    setIndeterminate,
    setSelectedValues,
  ]);

  const handleExport = useCallback(() => {
    const baseData = data;
    let exportData: any;
    let columnExcel: any[];

    if (isSelectAllRow) {
      exportData = baseData.filter(
        (i: any) =>
          !unCheckedListArr?.includes(
            typeof rowKey === 'string' ? i : rowKey(i),
          ),
      );
    } else {
      exportData = baseData;
    }

    if (entity === ENTITY.REPORTS && selectedValues.includes('Scores')) {
      columnExcel = columns.filter(col => col?.title !== 'Scores');
    } else {
      columnExcel = columns.filter(col =>
        selectedValues.includes(col?.title as string),
      );
    }

    if (transformData && !!products.length) {
      exportData = transformData(exportData, products);
    }

    let finalData;
    finalData = exportData.reduce((result: any[], item: any) => {
      const x: any = {};
      columnExcel.forEach(column => {
        const { render, renderColumnExcel, dataIndex } = column;
        let value: string | number = dataIndex ? _get(item, dataIndex, '') : '';
        if (dataIndex) {
          if (renderColumnExcel) {
            value = renderColumnExcel(_get(item, dataIndex), item, 0);
          } else {
            if (render) {
              const renderResult = renderCondition(
                render(_get(item, dataIndex), item, 0),
              );
              if (
                typeof renderResult === 'string' ||
                typeof renderResult === 'number'
              ) {
                value = renderResult;
              }
            }
          }
        }
        x[column?.title as string] = value;
      });
      result.push(x);
      return result;
    }, []);
    // }

    const now = new Date();

    let rewriteEntity = entity;
    if (entity === ENTITY.KITS) {
      const channelName =
        Object.keys(ChannelsName).find(key => {
          return ChannelsName[key] === baseData[0]?.channel?.name;
        }) || '';

      rewriteEntity = channelName && `${channelName}_${entity}`;
    }

    const fileName = `${rewriteEntity}_${now.getFullYear()}${
      now.getMonth() + 1
    }${now.getDate()}_${now.getHours()}${now.getMinutes()}${now.getSeconds()}`;

    exportExcel(finalData, fileName.toUpperCase());
    setAllPageRecords?.([]);
    setCheckedListOfAllPage?.({});
    setCheckedRecordByPage?.({});
    handleClose();
  }, [
    data,
    isSelectAllRow,
    entity,
    selectedValues,
    transformData,
    products,
    setAllPageRecords,
    setCheckedListOfAllPage,
    setCheckedRecordByPage,
    handleClose,
    unCheckedListArr,
    rowKey,
    columns,
  ]);

  const handleChangeSelectCol = useCallback(
    (list: any[]) => {
      setSelectedValues(list);
      setIndeterminate(!!list.length && list.length < transformCols.length);
      setCheckAll(list.length === transformCols.length);
    },
    [transformCols],
  );

  const handleCheckAllChange = (e: CheckboxChangeEvent) => {
    setSelectedValues(e.target.checked ? transformCols : []);
    setIndeterminate(false);
    setCheckAll(e.target.checked);
  };

  return (
    <CustomModalExportStyle
      title={t('common.selectColumnsToExport')}
      open={isOpen}
      onCancel={handleClose}
      footer={
        <CustomButton
          onClick={handleExport}
          disabled={!selectedValues.length}
          aria-label="Export btn"
          size="large"
          loading={!data.length}
        >
          {t('common.proceed')}
        </CustomButton>
      }
      centered
      width={470}
    >
      <CustomScrollbar style={{ maxHeight: 600 }}>
        <Checkbox
          indeterminate={indeterminate}
          onChange={handleCheckAllChange}
          checked={checkAll}
        >
          {t('common.allColumns')}
        </Checkbox>
        <CheckboxGroup
          style={{ display: 'flex', flexDirection: 'column' }}
          options={
            entity === ENTITY.REPORTS
              ? transformCols.slice(0, 6)
              : transformCols
          }
          value={selectedValues}
          onChange={handleChangeSelectCol}
        />
      </CustomScrollbar>
    </CustomModalExportStyle>
  );
};

export default ExportByColumnModalWithoutFetch;
